import { z } from "zod";

export type FormState = {
  // "done" is what `redirect()` used to be. A server action could end a
  // request by navigating; a function call cannot, so success comes back as
  // state and `useFormRedirect` moves the router.
  status: "idle" | "error" | "done";
  redirectTo?: string;
  fieldErrors: Record<string, string[] | undefined>;
  // React resets an uncontrolled form once its action resolves, so the
  // submitted values have to travel back through state to survive an error.
  values: Record<string, string>;
  // Checkbox groups post one entry per checked box, so they cannot ride in
  // `values` without widening it for every single-value field in the app.
  listValues?: Record<string, string[]>;
};

export const initialFormState: FormState = {
  status: "idle",
  fieldErrors: {},
  values: {},
};

export const errorState = (
  values: Record<string, string>,
  fieldErrors: Record<string, string[] | undefined>,
): FormState => ({ status: "error", fieldErrors, values });

export const doneState = (redirectTo?: string): FormState => ({
  status: "done",
  redirectTo,
  fieldErrors: {},
  values: {},
});

// The raw strings ride along on success too: a write can still fail after
// parsing, and that error has to repopulate the same form.
export type ParseResult<TValue> =
  | {
      wasParsed: true;
      value: TValue;
      values: Record<string, string>;
      listValues: Record<string, string[]>;
    }
  | { wasParsed: false; state: FormState };

// Checkbox groups post one entry per checked box, so those fields have to be
// read with getAll. Named explicitly rather than sniffed off the zod shape:
// the caller knows which controls it rendered, and reaching into zod internals
// to guess would break on the next minor version.
const readFields = (
  formData: FormData,
  names: readonly string[],
): Record<string, string> =>
  Object.fromEntries(
    names.map((name) => {
      const value = formData.get(name);

      return [name, typeof value === "string" ? value : ""];
    }),
  );

const readListFields = (
  formData: FormData,
  names: readonly string[],
): Record<string, string[]> =>
  Object.fromEntries(
    names.map((name) => [
      name,
      formData.getAll(name).filter((value) => typeof value === "string"),
    ]),
  );

// Field names come from the schema itself, so a field cannot be validated but
// left unread — the two lists can no longer drift apart.
export const parseFormData = <TSchema extends z.ZodObject>(
  schema: TSchema,
  formData: FormData,
  multiValueFields: readonly string[] = [],
): ParseResult<z.infer<TSchema>> => {
  const singleNames = Object.keys(schema.shape).filter(
    (name) => !multiValueFields.includes(name),
  );
  const values = readFields(formData, singleNames);
  const listValues = readListFields(formData, multiValueFields);
  const parsed = schema.safeParse({ ...values, ...listValues });

  return parsed.success
    ? { wasParsed: true, value: parsed.data, values, listValues }
    : {
        wasParsed: false,
        state: {
          ...errorState(values, z.flattenError(parsed.error).fieldErrors),
          listValues,
        },
      };
};
